import { log } from './config';

class GoogleMapsLoader {
  private static instance: GoogleMapsLoader;
  private loadPromise: Promise<void> | null = null;
  private isLoaded: boolean = false;
  
  private constructor() {}
  
  static getInstance(): GoogleMapsLoader {
    if (!GoogleMapsLoader.instance) {
      GoogleMapsLoader.instance = new GoogleMapsLoader();
    }
    return GoogleMapsLoader.instance;
  }
  
  /**
   * Ensures Google Maps is loaded before proceeding
   */
  async ensureLoaded(): Promise<void> {
    log('🔍 [GoogleMapsLoader] ensureLoaded called', {
      isLoaded: this.isLoaded,
      hasGoogle: !!window.google,
      hasGeocoder: !!window.google?.maps?.Geocoder,
      hasPromise: !!this.loadPromise
    });
    
    // Quick check if already loaded
    if (window.google?.maps?.Geocoder) {
      this.isLoaded = true;
      log('🔍 [GoogleMapsLoader] Already loaded, returning immediately');
      return;
    }
    
    if (this.loadPromise) {
      log('🔍 [GoogleMapsLoader] Returning existing load promise');
      return this.loadPromise;
    }
    
    log('🔍 [GoogleMapsLoader] Starting new load promise');
    this.loadPromise = this.waitForGoogleMaps();
    
    try {
      await this.loadPromise;
      this.isLoaded = true;
      log('🔍 [GoogleMapsLoader] Load complete!');
    } catch (error) {
      log('🔍 [GoogleMapsLoader] Load failed:', error);
      this.loadPromise = null;
      this.isLoaded = false;
      throw error;
    }
  }
  
  private async waitForGoogleMaps(): Promise<void> {
    const maxAttempts = 300; // 30 seconds total - generous for slow connections
    const checkInterval = 100; // ms
    
    for (let i = 0; i < maxAttempts; i++) {
      // Check both the Google Maps object and our custom flags
      if (window.google?.maps?.Geocoder || (window as any).__googleMapsLoaded) {
        if (i > 0) {
          log('✅ Google Maps loaded after', i * checkInterval, 'ms');
        }
        return;
      }
      
      // Check if there was an error loading
      if ((window as any).__googleMapsError) {
        log('❌ Google Maps failed to load with error:', (window as any).__googleMapsError);
        return;
      }
      
      if (i === 0) {
        log('⏳ Waiting for Google Maps to load...', {
          isLoading: (window as any).__googleMapsLoading,
          hasGoogle: !!window.google,
          hasMaps: !!window.google?.maps
        });
      }
      
      // Log every 10 attempts (1 second)
      if (i > 0 && i % 10 === 0) {
        log('🕑 Still waiting for Google Maps...', i * checkInterval, 'ms elapsed', {
          hasGoogle: !!window.google,
          hasMaps: !!window.google?.maps,
          hasGeocoder: !!window.google?.maps?.Geocoder,
          isLoading: (window as any).__googleMapsLoading,
          attempt: i,
          maxAttempts
        });
      }
      
      await new Promise(resolve => setTimeout(resolve, checkInterval));
    }
    
    // Don't throw error - just log warning and continue
    log('⚠️ Google Maps did not load after', maxAttempts * checkInterval, 'ms - will use fallback APIs');
  }
  
  isGoogleMapsLoaded(): boolean {
    return !!(window.google?.maps?.Geocoder);
  }
}

export const googleMapsLoader = GoogleMapsLoader.getInstance();